import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Boxes, FileText, LogOut, Menu, ArrowUpRight, ShieldAlert, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const customerNav = [
  { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/portal/assets", label: "Asset Tracking", icon: Boxes },
  { to: "/portal/quotes", label: "Quote History", icon: FileText },
];

const adminNav = [
  { to: "/portal/admin/quotes", label: "Manage Quotes", icon: ClipboardList },
  { to: "/portal/admin/assets", label: "Manage Assets", icon: ShieldAlert },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { navigate("/"); logout(); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-6 h-16 border-b border-border flex items-center">
        <Logo to="/" size="h-9" testId="portal-logo" />
      </div>
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {customerNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
                isActive ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`
            }
          >
            <n.icon size={16} /> {n.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <>
            <div className="px-4 pt-6 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Admin</div>
            {adminNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
                    isActive ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-4 py-3 mb-2">
          <div className="font-sans text-sm text-foreground truncate">{user?.name}</div>
          <div className="font-mono text-[11px] text-muted-foreground truncate">{user?.company || user?.email}</div>
        </div>
        <button onClick={doLogout} data-testid="portal-logout" className="w-full flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-destructive hover:bg-muted transition-colors">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="dark min-h-screen bg-background text-foreground flex">
      <aside className="hidden lg:block w-64 border-r border-border fixed inset-y-0 left-0 bg-card z-30">{SidebarInner}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="h-16 border-b border-border flex items-center justify-between px-5 md:px-8 sticky top-0 bg-background/90 backdrop-blur z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="portal-menu-toggle"><Menu size={22} /></button>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground hidden lg:block">Client Portal · Chain of Custody</div>
          <a href="/#quote" className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 font-mono text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-colors" data-testid="portal-new-quote">
            New Request <ArrowUpRight size={14} />
          </a>
        </header>
        <main className="p-5 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
